import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useUser } from "../context/UserContext";
import { STEADFAST_LOGO, COMPANY_NAME, COURSE_TITLE } from "../lib/brand";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function NameCapture() {
  const navigate = useNavigate();
  const { user, createUser, loading } = useUser();
  const [name, setName] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("learner_name") || "";
    } catch {
      return "";
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/home", { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please enter your full name to continue.");
      return;
    }
    if (trimmed.length > 80) {
      setError("Name is too long.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await createUser(trimmed);
      navigate("/home", { replace: true });
    } catch (e2) {
      console.error("Failed to create user:", e2);
      setError(e2?.response?.data?.detail || "Could not start. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || user) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-amber mb-4"></div>
        <p className="text-zinc-500 text-sm">
          {user ? "Redirecting..." : "Loading module..."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-background overflow-hidden" data-testid="name-capture-page">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none grain opacity-60" />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 h-[40rem] w-[40rem] rounded-full blur-3xl opacity-30"
        style={{
          background:
            "radial-gradient(circle at center, rgba(245,158,11,0.45) 0%, rgba(245,158,11,0) 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 h-[35rem] w-[35rem] rounded-full blur-3xl opacity-25"
        style={{
          background:
            "radial-gradient(circle at center, rgba(28,25,23,0.4) 0%, rgba(28,25,23,0) 70%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-2xl text-center">
            {/* Prominent logo */}
            <div
              className="mx-auto flex items-center justify-center"
              data-testid="landing-logo"
            >
              <img
                src={STEADFAST_LOGO}
                alt="Steadfast IT Consulting"
                className="h-28 sm:h-36 w-auto object-contain drop-shadow-xl"
              />
            </div>
            <div className="mt-4 text-xs sm:text-sm uppercase tracking-[0.3em] text-muted-foreground">
              {COMPANY_NAME}
            </div>

            <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-balance">
              {COURSE_TITLE}
              <span className="block italic text-brand-amber text-3xl sm:text-4xl lg:text-5xl mt-2">
                a 40-hour beginner journey
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto text-balance">
              Ten concept-first modules. Real-world stories, friendly diagrams, short quizzes
              and a shareable certificate at the end. Built for final-semester students — no
              coding background required.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 max-w-md mx-auto">
              <label htmlFor="student-name" className="block text-left text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
                Enter your full name to begin
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  id="student-name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Priya Sharma"
                  data-testid="student-name-input"
                  className="h-14 text-base rounded-xl"
                  maxLength={80}
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="start-course-btn"
                  className="h-14 px-8 rounded-full bg-brand-amber hover:bg-brand-amber-hover text-white dark:text-brand-ink text-base font-medium"
                >
                  {submitting ? "Starting…" : "Start learning"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              {error && (
                <div data-testid="name-error" className="mt-3 text-sm text-red-600 text-left">
                  {error}
                </div>
              )}
              <p className="mt-4 text-xs text-muted-foreground italic flex items-center justify-center gap-1.5">
                <Sparkles className="h-3 w-3 text-brand-amber" />
                Once submitted, your name will be locked for the certificate.
              </p>
            </form>
          </div>
        </div>

        <footer className="px-6 pb-8 text-center text-xs text-muted-foreground">
          Delivered by {COMPANY_NAME} · Learning portal v1.0
        </footer>
      </div>
    </div>
  );
}
